/**
 * Lightweight in-house analytics tracker.
 *
 * - Auto-tracks page views on every route change.
 * - Exposes `track(name, props)` for explicit business events
 *   (CTA clicks, form submits, conversions, etc.).
 *
 * Privacy: no third-party trackers, no cookies, no fingerprinting.
 * Identifiers are random uuids stored in localStorage / sessionStorage.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const VISITOR_KEY = "luxstay-analytics-vid";
const SESSION_KEY = "luxstay-analytics-sid";

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "vid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getVisitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) { v = uuid(); localStorage.setItem(VISITOR_KEY, v); }
    return v;
  } catch { return null; }
}

function getSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) { s = uuid(); sessionStorage.setItem(SESSION_KEY, s); }
    return s;
  } catch { return null; }
}

function readUtm() {
  try {
    const p = new URLSearchParams(window.location.search);
    const out = {};
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
      const v = p.get(k);
      if (v) out[k] = v;
    }
    return out;
  } catch { return {}; }
}

async function send(payload) {
  if (!BACKEND) return;
  try {
    await axios.post(`${BACKEND}/api/analytics/track`, payload, {
      withCredentials: true, timeout: 4000,
    });
  } catch {
    // Analytics must NEVER break the page. Silent failure is correct.
  }
}

/**
 * Public API: explicit event tracking.
 * Usage: `track("cta_click", { location: "homepage_hero", to: "/precos" })`
 */
export function track(name, props = {}) {
  const path = (typeof window !== "undefined" ? window.location.pathname : "") || "/";
  send({
    type: "event",
    name,
    path,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    props: { ...readUtm(), ...props },
  });
}

/**
 * Hook installed at the App root — auto-tracks page views on every
 * route change. No-op for admin/employee/client internal pages
 * (we still track those, the pricing dashboard separates by `user_role`).
 */
export default function useAnalytics() {
  const { pathname } = useLocation();
  useEffect(() => {
    send({
      type: "pageview",
      path: pathname,
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      props: readUtm(),
    });
  }, [pathname]);
}
